import { useAuthStore } from '@/stores/Auth'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { clearEmailDownloadToken, emailDownloadReturn } from '@/models/EmailDownload/Link'

// Pages where an emailed download link stays pending: the link itself and the pages for signing in to use it.
const emailDownloadRoutes = [
	'shared-download',
	'login',
	'register',
	'forgot-password',
	'verify-email-notice',
	'email-verification',
]

export default async (to: RouteLocationNormalizedLoaded, from?: RouteLocationNormalizedLoaded) => {
	const result = await access(to, from)
	// Reaching any other page abandons the link. Guards also run when a new document loads (Back, a typed URL).
	if (result === true && !emailDownloadRoutes.includes(String(to.name))) {
		clearEmailDownloadToken()
	}
	return result
}

async function access(to: RouteLocationNormalizedLoaded, from?: RouteLocationNormalizedLoaded) {
	const auth = useAuthStore()

	// Allow user to visit unprotected routes
	if (to.meta.unprotected) {
		return true
	}

	// Get the user if it's not already loaded
	if (!auth.userReturned) {
		await auth.getUser()
	}

	// Is user is not logged in
	if (!auth.user) {
		// If user is not logged in allow access of user avoidant routes
		if (to.meta.avoidUser) {
			return true
		}

		// If user is not logged in, redirect to login
		return { name: 'login' }
	}

	// If user is logged in but not verified redirect to verify email
	if (!auth.user.verified && to.name !== 'verify-email-notice') {
		return { name: 'verify-email-notice' }
	}

	const downloadReturn = emailDownloadReturn()
	if (
		auth.user.verified &&
		downloadReturn &&
		(to.meta.avoidUser || ['login', 'register', 'verify-email-notice'].includes(String(from?.name)))
	) {
		return downloadReturn
	}

	// If user is logged in prevent access of user avoidant routes
	if (to.meta.avoidUser) {
		return { name: 'root' }
	}

	// If user doesn't have required role, prevent access to route
	if (Array.isArray(to.meta.roles) && !to.meta.roles?.includes(auth.user.role)) {
		return { name: 'root' }
	}

	// If user doesn't have required feature flag, prevent access to route
	if (
		Array.isArray(to.meta.featureFlags) &&
		!to.meta.featureFlags.some((f: string) => auth.hasFeatureFlag(f))
	) {
		return { name: 'root' }
	}

	// If user is logged in, allow access to everything else
	return true
}
